import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useFetchPatient } from "@/infra/apis/patients/useFetchPatient";
import { useFetchActiveTreatment } from "@/infra/apis/treatments/useFetchActiveTreatment";
import { useAuth, useFetchTermsAndConditionsStatus, useNavigateToTermsAndConditions, errorTrackingService, Exceptions } from "@akina/infra";
import { LoadingOverlay, UseOnLargerScreenModal, useLocaleStore, useScreenSizeDetector } from "@akina/presentation";
import { useIntl } from "@/infra/locale";
import { analyticsService } from "@/infra/observability/analyticsService";
import { AppRoutes } from ".";
import { LOCALE_GET_PARAM } from "./utils";
import { checkInvalidPrescriptionState, checkPrescriptionExpired } from "@/utils/prescriptionUtils";
import { UpdatedTermsAcceptanceModal } from "@/components/legal/UpdatedTermsAcceptanceModal";
import { UpdatedTermsAndPrivacyPolicyAcceptanceModal } from "@/components/legal/UpdatedTermsAndPrivacyPolicyAcceptanceModal";
import { UpdatedPrivacyPolicyAcceptanceModal } from "@/components/legal/UpdatedPrivacyPolicyAcceptanceModal";

const ProtectedRoute = () => {
  const { pathname } = useLocation();

  // Data Fetching
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const { isLoading: isPatientLoading, data: patient, error: patientError } = useFetchPatient({ throwOnError: false });
  const { isLoading: isActiveTreatmentLoading, data: activeTreatment, error: activeTreatmentError } = useFetchActiveTreatment();
  const isProtectedDataLoading = isPatientLoading || isActiveTreatmentLoading;

  // Search Params
  const [searchParams] = useSearchParams();
  const locale = searchParams.get(LOCALE_GET_PARAM);
  const search = locale ? `?${LOCALE_GET_PARAM}=${locale}` : "";

  const isOnboardingVerificationPage = AppRoutes.OnboardingVerify === pathname;
  const isInvalidPrescriptionState = checkInvalidPrescriptionState(activeTreatment);
  const isPatientDisconnected = !isProtectedDataLoading && activeTreatmentError instanceof Exceptions[404];
  const isPrescriptionExpired = !isProtectedDataLoading && checkPrescriptionExpired(patient, patientError);

  usePatientInitialization(patient);

  if (isAuthLoading) {
    return <LoadingOverlay />;
  }
  if (!isAuthenticated) return <Navigate to={{ pathname: AppRoutes.Onboarding, search }} />;
  if (isOnboardingVerificationPage) return <Outlet />;
  if (!user?.email_verified) return <Navigate to={AppRoutes.OnboardingVerify} />;
  if (isProtectedDataLoading) {
    return <LoadingOverlay />;
  }

  if (isInvalidPrescriptionState && (pathname as AppRoutes) !== AppRoutes.InvalidPrescriptionState)
    return <Navigate to={AppRoutes.InvalidPrescriptionState} replace />;
  if (isPrescriptionExpired && (pathname as AppRoutes) !== AppRoutes.PrescriptionExpired)
    return <Navigate to={AppRoutes.PrescriptionExpired} replace />;
  if (
    !isPrescriptionExpired &&
    isPatientDisconnected &&
    (pathname as AppRoutes) !== AppRoutes.PatientDisconnected
  )
    return <Navigate to={AppRoutes.PatientDisconnected} replace />;

  return (
    <>
      <MobileGuard />
      <LegalModals />
      <Outlet />
    </>
  );
}

function MobileGuard() {
  const { isMobile } = useScreenSizeDetector();
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();

  if (!isMobile || isOnboardingOrLegalPage(pathname as AppRoutes)) return null;
  return <UseOnLargerScreenModal formatMessage={formatMessage} />;
}

function LegalModals() {
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyPolicyModalOpen, setPrivacyPolicyModalOpen] = useState(false);
  const [termsAndPrivacyPolicyModalOpen, setTermsAndPrivacyPolicyModalOpen] = useState(false);

  const { data: patientTerms } = useFetchTermsAndConditionsStatus();
  const { pathname } = useLocation();

  useNavigateToTermsAndConditions({
    terms: patientTerms,
    setTermsModalOpen,
    setPrivacyPolicyModalOpen,
    setTermsAndPrivacyPolicyModalOpen,
  });

  if (!patientTerms || isOnboardingOrLegalPage(pathname as AppRoutes)) return null;

  return (
    <>
      <UpdatedTermsAcceptanceModal
        patientTerms={patientTerms}
        open={termsModalOpen}
        onOpenChange={setTermsModalOpen}
      />
      <UpdatedPrivacyPolicyAcceptanceModal
        patientTerms={patientTerms}
        open={privacyPolicyModalOpen}
        onOpenChange={setPrivacyPolicyModalOpen}
      />
      <UpdatedTermsAndPrivacyPolicyAcceptanceModal
        open={termsAndPrivacyPolicyModalOpen}
        onOpenChange={setTermsAndPrivacyPolicyModalOpen}
      />
    </>
  );
}

function usePatientInitialization(patient: ReturnType<typeof useFetchPatient>["data"]) {
  const { locale, setLocale } = useLocaleStore();

  useEffect(() => {
    if (!patient?.patientId) return;
    analyticsService.identifyUser({ id: patient.patientId });
    errorTrackingService.identifyUser({ id: patient.patientId });
  }, [patient?.patientId]);

  useEffect(() => {
    if (patient?.preferences?.language && patient.preferences.language !== locale) {
      setLocale(patient.preferences.language);
    }
  }, [patient?.preferences?.language, locale, setLocale]);
}

function isOnboardingOrLegalPage(pathname: AppRoutes): boolean {
  return (
    pathname === AppRoutes.PrivacyPolicyAndDataTransparency ||
    pathname === AppRoutes.TermsAndConditions ||
    pathname === AppRoutes.OnboardingVerify ||
    pathname === AppRoutes.OnboardingTermsAndConditions ||
    pathname === AppRoutes.Onboarding
  );
}

export default ProtectedRoute;
